/**
 * GEN-2959 — contrato de URL dos links diretos (deep links) de REGISTRO.
 *
 * Os e-mails e as notificações do backend montam estes endereços; as telas leem os params no mount,
 * abrem o modal que já existe e LIMPAM o param (replace) para F5/voltar não reabrirem. Módulo sem
 * dependências de propósito (mesmo motivo de `kanban/registroDoVinculo`): qualquer tela importa sem
 * arrastar componente pesado.
 *
 * - `/certidoes?certidao=<guid_certificate>`
 * - `/certidoes?type=divida-ativa&dividaAtiva=<guid_company>` (GEN-4101: abre a Dívida Ativa da empresa)
 * - `/caixa-postal?mensagem=<guid_dte_message>` (GEN-3152; a constante mora em `registroDoVinculo`)
 * - `/kanban?card=<guid_card>[&comentario=<guid_comment>]`
 * - `/kanban-v2/quadros/<b>/cards/<c>[?comentario=<id>]` (rota do K2; o foco no comentário é da lib)
 * - `cliente=<guid_client>` em QUALQUER um deles: o AppShell troca para esse cliente (se o usuário tem
 *   acesso) antes de a tela resolver o registro.
 *
 * A URL é não-confiável: o param só abre o que o backend devolver para o colaborador logado.
 */
import { isSafeInternalPath } from "../_services/returnTo";

export const PARAM_CLIENTE = "cliente";
export const PARAM_CERTIDAO = "certidao";
/** GEN-4101 — Dívida Ativa de uma empresa (o aviso de movimentação leva direto ao painel dela). */
export const PARAM_DIVIDA_ATIVA = "dividaAtiva";
export const PARAM_CARD = "card";
export const PARAM_COMENTARIO = "comentario";

/**
 * Status de "o backend respondeu que não há registro para você" (inexistente, fora do cliente, sem
 * alcance). Qualquer outro não-200 é falha de verdade (rede, 5xx).
 */
export const STATUS_NAO_ENCONTRADO = [400, 403, 404];

/**
 * O id do registro vem da URL (não-confiável) e vai para o CAMINHO da chamada à API
 * (`/cnd/get/<cliente>/<id>`). Só passa o formato de guid: `..%2F` ou `?` no id faria o navegador
 * chamar OUTRA rota com o token de quem clicou. Fora do formato = registro não encontrado.
 */
export const ehGuid = (valor: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(valor);

/** Cópia de `search` sem os params informados (os demais — filtros da tela — ficam). */
export const semParams = (search: URLSearchParams | string, nomes: string[]): URLSearchParams => {
  const restantes = new URLSearchParams(search);
  nomes.forEach((nome) => restantes.delete(nome));
  return restantes;
};

/**
 * Caminho interno (`/certidoes?certidao=…`) do `linkToPlatform` de uma notificação, ou `null`.
 *
 * O backend manda URL absoluta (endereço da plataforma do ambiente que gerou o e-mail). Só o caminho
 * interessa — a navegação é sempre DENTRO do app, então o host do link não decide nada e não há
 * redirecionamento aberto. Link que leva só à home ou a caminho inseguro → sem botão.
 */
export const caminhoInternoDoLink = (link: unknown): string | null => {
  if (typeof link !== "string" || !link.trim()) return null;
  let url: URL;
  try {
    url = new URL(link, window.location.origin);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  const caminho = `${url.pathname}${url.search}${url.hash}`;
  if (url.pathname === "/" || !isSafeInternalPath(caminho)) return null;
  return caminho;
};

/** `?a=b` ou "" — para remontar `pathname + search` sem deixar um `?` pendurado. */
export const comoSearch = (params: URLSearchParams): string => {
  const s = params.toString();
  return s ? `?${s}` : "";
};
